'use client';

import React, {
  type InputHTMLAttributes,
  type PropsWithChildren,
  useEffect,
  useRef,
} from 'react';
import { mergeProps, useId, VisuallyHidden } from 'react-aria';
import clsx from 'clsx';
import { Stack } from 'components/Stack';
import type { TextAlign } from 'components/Text/types';
import inputStyles from 'styles/input.module.css';
import { isComposingKeyDown } from 'utils/is-composing';
import { focusWithoutScrolling, mergeRefs } from '@react-aria/utils';
import { InlineError } from './InlineError';
import { InputAdornment } from './InputAdornment';
import { InputErrorAdornment } from './InputErrorAdornment';
import { TextFieldHelp } from './TextFieldHelp';
import { TextFieldLabel } from './TextFieldLabel';
import type { TextFieldErrorTypes, TextFieldSize } from './types';

export type TextFieldProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size'
> &
  PropsWithChildren<{
    /**
     * determine the vertical sizing of the component
     */
    size?: TextFieldSize;
    /**
     * the displayed decoration at the start of the field
     */
    adornmentBefore?: React.ReactNode;
    /**
     * the displayed decoration at the end of the field
     */
    adornmentAfter?: React.ReactNode;
    /**
     * the displayed help text
     */
    helpText?: React.ReactNode;
    /**
     * the displayed error message
     */
    error?: string;
    /**
     * determine the style of the error message
     */
    errorVariant?: TextFieldErrorTypes;
    /**
     * enable so the input will be focused when the component mounts
     */
    autoFocusWithoutScrolling?: boolean;
    /**
     * disable the primary color around the focused state
     */
    muted?: boolean;
    /**
     * enable so the input value will be trimmed when the input loses focus
     */
    autoTrim?: boolean;
    /** Defines the width of the input field, in characters */
    inputSize?: number;
    /**
     * define the alignment of the value within the input field
     */
    align?: TextAlign;
    /**
     * The displayed content of the field input's label
     */
    label?: string;
    /**
     * If enabled, the label is visually hidden but remains accessible to screen readers
     */
    hideLabel?: boolean;
  }>;

export const TextField = React.forwardRef(
  (
    {
      adornmentAfter,
      adornmentBefore,
      autoFocusWithoutScrolling,
      className,
      disabled,
      error,
      helpText,
      hideLabel,
      id: idProp,
      inputSize,
      label,
      placeholder,
      style,
      align = 'left',
      autoTrim = false,
      errorVariant = 'tooltip',
      muted = false,
      size = 'medium',
      onKeyDown: onKeyDownProp,
      ...other
    }: TextFieldProps,
    forwardedRef: any
  ) => {
    const inputId = useId(idProp);
    const errorId = useId();
    const containerRef = useRef(null);
    const ownRef = useRef(null);
    const hasError = Boolean(error);
    const ref = mergeRefs(forwardedRef, ownRef);
    const mergedProps = mergeProps(
      {
        onBlur: () => {
          if (autoTrim) {
            // @ts-expect-error FIXME(strictNullChecks)
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
              window.HTMLInputElement.prototype,
              'value'
            ).set;
            // @ts-expect-error FIXME(strictNullChecks)
            nativeInputValueSetter.call(
              ownRef.current,
              // @ts-expect-error FIXME(strictNullChecks)
              ownRef.current.value.trim()
            );

            const ev2 = new Event('input', { bubbles: true });
            // @ts-expect-error FIXME(strictNullChecks)
            ownRef.current.dispatchEvent(ev2);
          }
        },
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
          /* Stop propagation for ArrowUp, ArrowDown, Enter, and Escape keys
           * while isComposing to avoid interfering with IME input (for
           * languages like Japanese where composition is common).
           * This enables the user to:
           * - navigate (ArrowUp/ArrowDown) the composition results
           * - cancel/abort the current composition (Escape)
           * - select the currently highlighted composition result (Enter)
           */
          if (isComposingKeyDown(e)) return;
          onKeyDownProp?.(e);
        },
      },
      other
    );

    const labelNode = getLabelNode(inputId, label, hideLabel);
    const ariaLabel = label ?? placeholder;
    useEffect(() => {
      // @ts-expect-error FIXME(strictNullChecks)
      if (autoFocusWithoutScrolling) focusWithoutScrolling(ownRef.current);
    }, [autoFocusWithoutScrolling]);

    return (
      <>
        <Stack gap={4}>
          {labelNode}
          <div
            ref={containerRef}
            className={clsx(
              inputStyles.inputContainerRoot,
              inputStyles.inputContainer,
              inputStyles.inputContainerBorder,
              inputStyles.inputContainerBackground,
              className
            )}
            data-disabled={disabled || undefined}
            data-has-error={hasError || undefined}
            data-muted={muted || undefined}
            data-vapor-text-field
            style={style}
          >
            {adornmentBefore && (
              <InputAdornment $placement="start">
                {adornmentBefore}
              </InputAdornment>
            )}
            <input
              ref={ref}
              aria-describedby={hasError ? errorId : undefined}
              aria-invalid={hasError || undefined}
              aria-label={ariaLabel}
              autoComplete="off"
              className={clsx(inputStyles.input, inputStyles.inputSize)}
              data-size={size}
              disabled={disabled}
              id={inputId}
              placeholder={placeholder}
              size={inputSize}
              style={{ textAlign: align }}
              {...mergedProps}
            />
            <InputErrorAdornment error={error} errorVariant={errorVariant} />
            {adornmentAfter && (
              <InputAdornment $placement="end">{adornmentAfter}</InputAdornment>
            )}
          </div>
        </Stack>
        <InlineError
          error={error}
          errorId={errorId}
          errorVariant={errorVariant}
          hasError={hasError}
        />
        {helpText && (
          <TextFieldHelp paddingLeft={1} paddingTop={4}>
            {helpText}
          </TextFieldHelp>
        )}
      </>
    );
  }
);

/**
 * @private
 * @description determine if we should visually hide a field label but keep it accessible to screen readers
 */
function getLabelNode(
  inputId: string,
  displayedLabel?: TextFieldProps['label'],
  shouldHideLabel?: TextFieldProps['hideLabel']
) {
  if (!displayedLabel) return null;
  return shouldHideLabel ? (
    <VisuallyHidden>
      <TextFieldLabel htmlFor={inputId}>{displayedLabel}</TextFieldLabel>
    </VisuallyHidden>
  ) : (
    <TextFieldLabel htmlFor={inputId}>{displayedLabel}</TextFieldLabel>
  );
}
